/**
 * Separadores de día del chat, estilo WhatsApp: los mensajes solo mostraban la
 * hora, así que una charla de varios días se leía como si fuera toda seguida.
 *
 * Gemelo de packages/mobile/utils/chatDay.ts — si tocás uno, tocá el otro.
 */

export interface ChatDayLabels {
  today: string;
  yesterday: string;
}

const DEFAULT_LABELS: ChatDayLabels = { today: 'Hoy', yesterday: 'Ayer' };

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "Hoy" / "Ayer" / "DD/MM/YYYY" (formato DD/MM, nunca MM/DD). */
export function chatDayLabel(
  iso: string,
  labels: ChatDayLabels = DEFAULT_LABELS,
  now: Date = new Date()
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  if (isSameDay(d, now)) return labels.today;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDay(d, yesterday)) return labels.yesterday;

  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** Clave estable del día (YYYY-MM-DD en hora local) para keys de lista. */
export function chatDayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'invalid';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Intercala un separador antes del primer elemento de cada día. Espera los
 * items ya ordenados cronológicamente.
 */
export function withDaySeparators<T extends { createdAt: string }>(
  items: T[],
  labels: ChatDayLabels = DEFAULT_LABELS,
  now: Date = new Date()
): Array<T | { kind: 'day'; key: string; createdAt: string; label: string }> {
  const out: Array<T | { kind: 'day'; key: string; createdAt: string; label: string }> = [];
  let lastDay: string | null = null;

  for (const item of items) {
    const day = chatDayKey(item.createdAt);
    if (day !== 'invalid' && day !== lastDay) {
      out.push({
        kind: 'day',
        key: `d_${day}`,
        createdAt: item.createdAt,
        label: chatDayLabel(item.createdAt, labels, now),
      });
      lastDay = day;
    }
    out.push(item);
  }

  return out;
}
